import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ReceiptText, Loader2, AlertTriangle } from "lucide-react";
import { formatDMY, todayISO } from "@/lib/dateFormat";

const MOTIVOS = [
  { v: "level_mismatch", l: "El nivel no corresponde al grupo" },
  { v: "group_change", l: "Cambio de grupo" },
  { v: "withdrawal", l: "Retiro del programa" },
  { v: "billing_error", l: "Error de facturación" },
  { v: "other", l: "Otro" },
];

const METODOS = [
  { v: "cash", l: "Efectivo" },
  { v: "transfer", l: "Transferencia" },
  { v: "sinpe", l: "SINPE Móvil" },
  { v: "card_reversal", l: "Reverso a la tarjeta" },
];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/** Nota de crédito contra una factura: devuelve la parte que el estudiante no consumió. */
export const CreditNoteDialog = ({ payment, onOpenChange, onDone }) => {
  const [prev, setPrev] = useState(null);
  const [form, setForm] = useState({
    classes_consumed: 0, amount: "", reason: "level_mismatch", reason_note: "",
    refund_method: "cash", refund_date: todayISO(),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!payment?.id) return;
    setPrev(null);
    api.get(`/payments/${payment.id}/credit-note-preview`)
      .then(({ data }) => {
        setPrev(data);
        setForm((f) => ({
          ...f,
          classes_consumed: data.classes_consumed || 0,
          amount: String(data.suggested_amount ?? data.max_amount ?? 0),
        }));
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo leer la factura"));
  }, [payment?.id]);

  // El crédito se recalcula al cambiar las clases tomadas, pero el monto queda editable.
  const recalcular = (consumidas) => {
    const n = Math.max(0, Math.min(Number(consumidas) || 0, prev?.classes_included || 0));
    const sugerido = prev?.classes_included
      ? Math.max(Number(prev.amount) - Number(prev.unit_price) * n, 0)
      : Number(prev?.max_amount || 0);
    setForm((f) => ({
      ...f,
      classes_consumed: n,
      amount: String(Math.min(sugerido, Number(prev?.max_amount || 0)).toFixed(2)),
    }));
  };

  const emitir = async () => {
    const monto = Number(form.amount);
    if (!(monto > 0)) { toast.error("Poné el monto a devolver"); return; }
    if (monto > Number(prev?.max_amount || 0) + 0.009) {
      toast.error(`Esa factura solo admite ${money(prev?.max_amount)} más`);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/credit-notes", {
        payment_id: payment.id,
        amount: monto,
        reason: form.reason,
        reason_note: form.reason_note,
        classes_included: prev?.classes_included || null,
        classes_consumed: form.classes_consumed,
        refund_method: prev?.paid ? form.refund_method : "none",
        refund_date: prev?.paid ? form.refund_date : null,
      });
      toast.success(`${data.display_number} emitida por ${money(data.amount)}`);
      onOpenChange(false);
      if (onDone) onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo emitir la nota de crédito");
    } finally {
      setSaving(false);
    }
  };

  const consumido = prev?.classes_included
    ? Number(prev.unit_price) * Number(form.classes_consumed || 0)
    : 0;

  return (
    <Dialog open={!!payment} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="credit-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ReceiptText className="h-5 w-5 text-rose-600" />
            Nota de crédito
          </DialogTitle>
          <DialogDescription>
            {prev
              ? <>Factura <b>{prev.invoice_number || "—"}</b> · {prev.student_name} · {money(prev.amount)}</>
              : "Cargando la factura…"}
          </DialogDescription>
        </DialogHeader>

        {!prev ? (
          <div className="py-8 text-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-4">
            {prev.enrollment_missing && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5"
                 data-testid="credit-note-no-program">
                El programa de esta factura ya no existe, así que la asistencia no se puede leer:
                escribí a mano cuántas clases alcanzó a tomar.
              </p>
            )}

            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-[13px]">
              <div className="flex justify-between px-3 py-2">
                <span className="text-slate-500">Clases incluidas</span>
                <span className="font-medium tabular-nums">{prev.classes_included || "—"}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2">
                <Label htmlFor="cn-consumed" className="text-slate-500 font-normal">Clases consumidas</Label>
                <Input id="cn-consumed" type="number" min="0" max={prev.classes_included || undefined}
                       value={form.classes_consumed}
                       onChange={(e) => recalcular(e.target.value)}
                       className="h-8 w-20 text-right tabular-nums"
                       data-testid="credit-note-consumed-input" />
              </div>
              <div className="flex justify-between px-3 py-2">
                <span className="text-slate-500">Valor por sesión</span>
                <span className="font-medium tabular-nums" data-testid="credit-note-unit">
                  {money(prev.unit_price)}
                </span>
              </div>
              <div className="flex justify-between px-3 py-2">
                <span className="text-slate-500">Consumido</span>
                <span className="font-medium tabular-nums">{money(consumido)}</span>
              </div>
              {prev.already_credited > 0 && (
                <div className="flex justify-between px-3 py-2 text-rose-700">
                  <span>Ya acreditado antes</span>
                  <span className="font-medium tabular-nums">−{money(prev.already_credited)}</span>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="cn-amount">Crédito a devolver</Label>
              <Input id="cn-amount" type="number" step="0.01" min="0" max={prev.max_amount}
                     value={form.amount}
                     onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                     className="mt-1 text-lg font-semibold tabular-nums"
                     data-testid="credit-note-amount-input" />
              <p className="text-[11px] text-slate-500 mt-1">
                Máximo {money(prev.max_amount)}. Podés bajarlo si cobrás alguna penalidad.
              </p>
            </div>

            <div>
              <Label>Motivo</Label>
              <Select value={form.reason} onValueChange={(v) => setForm((f) => ({ ...f, reason: v }))}>
                <SelectTrigger className="mt-1" data-testid="credit-note-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOTIVOS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea value={form.reason_note} rows={2} className="mt-2"
                        placeholder="Detalle (opcional): queda en la nota y en el historial"
                        onChange={(e) => setForm((f) => ({ ...f, reason_note: e.target.value }))}
                        data-testid="credit-note-note-input" />
            </div>

            {prev.paid ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Devolución</Label>
                  <Select value={form.refund_method}
                          onValueChange={(v) => setForm((f) => ({ ...f, refund_method: v }))}>
                    <SelectTrigger className="mt-1" data-testid="credit-note-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {METODOS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="cn-date">Fecha de entrega</Label>
                  <Input id="cn-date" type="date" value={form.refund_date} className="mt-1"
                         onChange={(e) => setForm((f) => ({ ...f, refund_date: e.target.value }))}
                         data-testid="credit-note-refund-date-input" />
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex gap-2"
                 data-testid="credit-note-unpaid">
                <AlertTriangle className="h-3.5 w-3.5 text-slate-500 shrink-0 mt-0.5" />
                Esta factura no está pagada, así que no hay plata que devolver: la nota le baja lo
                que debe y no toca la caja.
              </p>
            )}

            <p className="text-[11px] text-slate-500">
              La venta baja en el mes en que emitís la nota
              {prev.paid && form.refund_date ? ` y la caja el ${formatDMY(form.refund_date, "es")}` : ""}.
              La nota gasta consecutivo propio y no se puede borrar: solo anular.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="credit-note-cancel-btn">Cancelar</Button>
          <Button onClick={emitir} disabled={saving || !prev}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="credit-note-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Emitir nota de crédito"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
